import React, { useEffect, useState, memo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { ResponsiveImage } from '../../components';
import css from './ListintImageLayout.module.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { Modal } from '../../components';

const ProgressiveImage = memo(({
  image,
  alt,
  variantPrefix,
  sizes,
  className = '',
  onClick = null,
  wrapperStyle = {},
  imgStyle = {},
}) => {
  // Collect all high-quality variants for the responsive engine.
  const variants = image?.attributes?.variants
    ? Object.keys(image.attributes.variants).filter(
      k =>
        k.startsWith(variantPrefix) ||
        k.startsWith('scaled-') ||
        k === 'facebook' ||
        k === 'twitter'
    )
    : [];

  return (
    <div
      className={classNames(css.imgWrapper, className)}
      style={wrapperStyle}
      onClick={onClick}
    >
      <ResponsiveImage
        image={image}
        alt={alt}
        variants={variants}
        sizes={sizes}
        style={imgStyle}
        className={css.imageLoaded}
      />
    </div>
  );
}
);

const ListintImageLayout = ({
  currentListing,
  variantPrefix,
  dynamicHeight,
  onManageDisableScrolling,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [desktopOpenModal, setDesktopOpenModal] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [openImageCarousel, setOpenImageCarousel] = useState(false);
  const [carouselImageIndex, setCarouselImageIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const intl = useIntl();

  const imagesUrl = currentListing?.images || [];
  const imagesUrlGrid = imagesUrl.slice(0, 5);

  // Default no-op handler if not provided
  const handleManageDisableScrolling = onManageDisableScrolling || (() => { });

  /**
   * Resolve the best available URL for a given image, preferring higher-res variants
   * to avoid blurry rendering on large display areas.
   * Falls back through: scaled-large → scaled-xlarge → variantPrefix (thumbnail)
   */
  const getSharpImageUrl = image => {
    if (!image?.attributes?.variants) return null;
    const variants = image.attributes.variants;
    return (
      variants['scaled-large']?.url ||
      variants['scaled-xlarge']?.url ||
      variants[variantPrefix]?.url ||
      null
    );
  };

  /**
   * For the lightbox modal use the highest-res variant available.
   */
  const getModalImageUrl = image => {
    if (!image?.attributes?.variants) return null;
    const variants = image.attributes.variants;
    return (
      variants['scaled-xlarge']?.url ||
      variants['scaled-large']?.url ||
      variants[variantPrefix]?.url ||
      null
    );
  };

  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape') {
        setOpenModal(false);
      }
    };

    if (openModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openModal]);

  // Slide to the correct image when carousel modal opens
  useEffect(() => {
    if (openImageCarousel && swiperInstance && carouselImageIndex >= 0) {
      swiperInstance.slideTo(carouselImageIndex, 0);
    }
  }, [openImageCarousel, carouselImageIndex, swiperInstance]);

  const getLayoutClass = () => {
    switch (imagesUrl.length) {
      case 1:
        return css.oneColumn;
      case 2:
        return css.twoColumn;
      case 3:
        return css.threeColumn;
      case 4:
        return css.fourColumn;
      case 5:
        return css.fiveColumn;
      default:
        return css.fiveColumn;
    }
  };

  return (
    <>
      <div className={css.desktopLayout}>

        <div className={`${css.listingImageLayout} ${getLayoutClass()}`}>
          {imagesUrlGrid?.map((i, index) => (
            <ProgressiveImage
              key={i.id?.uuid || `img-${index}`}
              image={i}
              alt="listing image"
              variantPrefix={variantPrefix}
              sizes="(max-width: 767px) 100vw, (max-width: 1024px) 66vw, (max-width: 1400px) 50vw, 800px"
            />
          ))}

          <button onClick={() => setDesktopOpenModal(true)} className={css.viewAllBtn}>
            {intl.formatMessage({ id: 'ListingImageLayout.seeAllPhotos' }, { count: imagesUrl.length })}
          </button>
        </div>

        {desktopOpenModal && (
          <div
            className={css.modalOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Image gallery"
          // onClick={() => setOpenModal(false)}
          >
            <div className={css.modalContent} onClick={(e) => e.stopPropagation()}>
              <button
                className={css.closeBtn}
                aria-label="Close gallery"
                onClick={() => setDesktopOpenModal(false)}
              >
                ×
              </button>

              <div className={css.mainImageContainer}>
                <ResponsiveImage
                  image={imagesUrl[activeImageIndex]}
                  alt={`Image ${activeImageIndex + 1} of ${imagesUrl.length}`}
                  // Use all variants for the modal to ensure maximum sharpness
                  variants={Object.keys(imagesUrl[activeImageIndex]?.attributes?.variants || {})}
                  // Full viewport width for modal
                  sizes="100vw"
                  className={css.mainImage}
                />
              </div>

              <div className={css.thumbnailContainer}>
                {imagesUrl.map((image, index) => (
                  <div
                    key={image.id?.uuid || `thumb-${index}`}
                    className={`${css.thumbnail} ${index === activeImageIndex ? css.activeThumbnail : ''}`}
                    onClick={() => setActiveImageIndex(index)}
                    role="button"
                    tabIndex={0}
                    aria-label={`View image ${index + 1}`}
                    onKeyDown={(e) => e.key === 'Enter' && setActiveImageIndex(index)}
                  >
                    <ResponsiveImage
                      image={image}
                      alt={`Thumbnail ${index + 1}`}
                      variants={['square-small', 'square-small2x']}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>


      <div className={css.mobileLayout}>
        <Swiper
          navigation={true}
          modules={[Navigation]}
          className={css.listingImagesCaroulse}
          spaceBetween={0}
          slidesPerView={1}
          onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex + 1)}
        >
          {imagesUrl?.map((image, index) => (
            <SwiperSlide key={image.id?.uuid || `slide-${index}`}>
              <ProgressiveImage
                image={image}
                alt={`listing image ${index + 1}`}
                variantPrefix={variantPrefix}
                // Full-width carousel on mobile/tablet
                sizes="(max-width: 1024px) 100vw, 800px"
                // Apply the dynamic height to the img element (not just the wrapper)
                // so the CSS .listingImagesCaroulse .imgWrapper rule is also satisfied
                wrapperStyle={{
                  height: `${dynamicHeight || 300}px`,
                  transition: 'height 0.3s ease-in-out',
                }}
                imgStyle={{
                  height: `${dynamicHeight || 300}px`,
                  transition: 'height 0.3s ease-in-out',
                  objectFit: 'cover',
                  width: '100%',
                }}
                onClick={() => {
                  setActiveImageIndex(index);
                  setOpenModal(true);
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {currentSlideIndex >= 1 ? (
          <div className={css.totalImagesNcurrent}>
            {currentSlideIndex} / {imagesUrl?.length || 0}
          </div>
        ) : null}
      </div>

      <Modal
        id="ListingImageModal"
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={handleManageDisableScrolling}
      >
        <div className={css.viewAllImagesMobile}>
          <div className={css.imageContainer}>
            {imagesUrl.map((image, index) => (
              <div
                key={image.id?.uuid || `thumb-${index}`}
                className={css.imgWrapper}
                onClick={() => {
                  setCarouselImageIndex(index);
                  setOpenImageCarousel(true);
                }}
              >
                <img
                  src={image?.attributes?.variants?.['square-small']?.url}
                  alt={`Thumbnail ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        id="ImageCarouselModal"
        isOpen={openImageCarousel}
        onClose={() => setOpenImageCarousel(false)}
        usePortal

        className={css.swiperViewAllModal}
        onManageDisableScrolling={handleManageDisableScrolling}
      >
        <div className={css.imageCarouselModal}>
          <button className={css.closeBtn} onClick={() => setOpenImageCarousel(false)}>
            ×
          </button>

          <Swiper
            onSwiper={setSwiperInstance}
            navigation={true}
            modules={[Navigation]}
            className={css.imageCarouselSwiper}
            spaceBetween={0}
            slidesPerView={1}
          >
            {imagesUrl?.map((image, index) => (
              <SwiperSlide key={image.id?.uuid || `carousel-${index}`}>
                <div className={css.carouselImageContainer}>
                  <img
                    src={getModalImageUrl(image)}
                    alt={`Image ${index + 1}`}
                    className={css.carouselImage}
                    onError={e => {
                      const fallback = image?.attributes?.variants?.[variantPrefix]?.url;
                      if (fallback && e.target.src !== fallback) {
                        e.target.src = fallback;
                      }
                    }}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Modal>
    </>
  );
};

export default ListintImageLayout;
